/**
 * Challenge Types
 * Defines all types for the challenges module
 */

/**
 * Challenge type options
 */
export type ChallengeType = "photo" | "quiz" | "location_checkin" | "task" | "puzzle" | "opdracht";

/**
 * Section type options for multi-part opdrachten
 */
export type SectionType = "checkbox" | "photo" | "text" | "number" | "location";

/**
 * Who can complete this challenge
 */
export type ChallengeTargetType = "individual" | "team";

/**
 * Who can see this challenge
 */
export type ChallengeVisibility = "all" | "specific_team" | "specific_users";

/**
 * Challenge status
 */
export type ChallengeStatus = "draft" | "active" | "completed" | "archived";

/**
 * Completion status
 */
export type CompletionStatus = "pending" | "approved" | "rejected" | "auto_approved";

/**
 * Photo challenge config
 */
export interface PhotoChallengeConfig {
  requirements: string[];
  verification_type: "auto" | "admin";
  min_people: number;
  require_location: boolean;
  example_image_url?: string;
}

/**
 * Quiz challenge config
 */
export interface QuizChallengeConfig {
  question: string;
  type: "multiple_choice" | "open" | "true_false";
  answers: Array<{ text: string; is_correct: boolean }>;
  max_attempts: number;
  hint_on_wrong?: string;
}

/**
 * Location check-in challenge config
 */
export interface LocationChallengeConfig {
  lat: number;
  lng: number;
  radius_meters: number;
  hint?: string;
  address?: string;
  is_secret: boolean;
}

/**
 * Task challenge config
 */
export interface TaskChallengeConfig {
  instructions: string;
  proof_type: "photo" | "code" | "admin_verify";
  verification_code?: string;
  is_competitive: boolean;
  ranking_criteria?: string;
}

/**
 * Puzzle challenge config
 */
export interface PuzzleChallengeConfig {
  puzzle_type: "word_scramble" | "number_code" | "rebus";
  solution: string;
  display: string;
  timed_hints?: Array<{ after_seconds: number; hint: string }>;
}

/**
 * Opdracht (multi-section) challenge config
 */
export interface OpdrachtConfig {
  /** Explainer text shown at top of opdracht */
  explainer?: string;
  /** Must complete sections in order? */
  require_order: boolean;
  /** Can earn points for partial completion? */
  allow_partial_points: boolean;
  /** How admin reviews: per section or whole opdracht */
  review_mode: "per_section" | "whole";
  /** Enable per-section time limits? */
  section_time_limits: boolean;
}

/**
 * Section-specific config for different section types
 */
export interface SectionConfig {
  // For text type
  min_length?: number;
  max_length?: number;
  placeholder?: string;
  
  // For number type
  min_value?: number;
  max_value?: number;
  unit?: string;
  
  // For photo type
  require_location?: boolean;
  min_people?: number;
  
  // For location type
  target_lat?: number;
  target_lng?: number;
  radius_meters?: number;
}

/**
 * Challenge section for multi-part opdrachten
 */
export interface ChallengeSection {
  id: string;
  challenge_id: string;
  title: string;
  description: string | null;
  section_type: SectionType;
  config: SectionConfig;
  order_index: number;
  is_required: boolean;
  points: number;
  time_limit_minutes: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * Section completion record
 */
export interface SectionCompletion {
  id: string;
  section_id: string;
  completion_id: string | null;
  completed_by: string;
  team_id: string | null;
  submission: SectionSubmission;
  status: CompletionStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  points_earned: number;
  started_at: string | null;
  completed_at: string;
  created_at: string;
}

/**
 * Section submission data types
 */
export interface CheckboxSubmission {
  completed: boolean;
}

export interface TextSectionSubmission {
  answer: string;
}

export interface NumberSectionSubmission {
  value: number;
}

export interface PhotoSectionSubmission {
  photo_url: string;
  location?: { lat: number; lng: number };
}

export interface LocationSectionSubmission {
  checked_in_at: { lat: number; lng: number };
  distance_meters?: number;
}

export type SectionSubmission =
  | CheckboxSubmission
  | TextSectionSubmission
  | NumberSectionSubmission
  | PhotoSectionSubmission
  | LocationSectionSubmission;

/**
 * Section with completion status
 */
export interface SectionWithStatus extends ChallengeSection {
  completion?: SectionCompletion | null;
  is_completed: boolean;
  can_complete: boolean;
}

/**
 * Opdracht with sections and progress
 */
export interface OpdrachtWithProgress extends Challenge {
  sections: SectionWithStatus[];
  total_points: number;
  earned_points: number;
  completed_sections: number;
  total_sections: number;
  progress_percentage: number;
}

/**
 * Union type for all challenge configs
 */
export type ChallengeConfig =
  | PhotoChallengeConfig
  | QuizChallengeConfig
  | LocationChallengeConfig
  | TaskChallengeConfig
  | PuzzleChallengeConfig
  | OpdrachtConfig;

/**
 * Main Challenge interface
 */
export interface Challenge {
  id: string;
  weekend_id: string | null;
  title: string;
  description: string | null;
  challenge_type: ChallengeType;
  target_type: ChallengeTargetType;
  visibility: ChallengeVisibility;
  visible_to_team_id: string | null;
  visible_to_user_ids: string[];
  has_points: boolean;
  points: number;
  bonus_points: number;
  available_from: string | null;
  available_until: string | null;
  time_limit_minutes: number;
  status: ChallengeStatus;
  config: ChallengeConfig;
  linked_event_id: string | null;
  reveal_targets: string[];
  notification_lottie: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Photo submission data
 */
export interface PhotoSubmission {
  photo_url: string;
  location?: { lat: number; lng: number };
  notes?: string;
}

/**
 * Quiz submission data
 */
export interface QuizSubmission {
  selected_answer: string;
  attempts: number;
}

/**
 * Location submission data
 */
export interface LocationSubmission {
  checked_in_at: { lat: number; lng: number };
  distance_meters: number;
}

/**
 * Task submission data
 */
export interface TaskSubmission {
  proof_url?: string;
  notes?: string;
  code_entered?: string;
}

/**
 * Puzzle submission data
 */
export interface PuzzleSubmission {
  answer: string;
  hints_used: number;
  time_seconds: number;
}

/**
 * Union type for all submission data
 */
export type ChallengeSubmission =
  | PhotoSubmission
  | QuizSubmission
  | LocationSubmission
  | TaskSubmission
  | PuzzleSubmission;

/**
 * Challenge completion record
 */
export interface ChallengeCompletion {
  id: string;
  challenge_id: string;
  completed_by: string;
  team_id: string | null;
  submission: ChallengeSubmission;
  status: CompletionStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  points_earned: number;
  bonus_earned: number;
  started_at: string | null;
  completed_at: string;
  created_at: string;
}

/**
 * Challenge with completion info (for list views)
 */
export interface ChallengeWithStatus extends Challenge {
  completion?: ChallengeCompletion | null;
  is_completed: boolean;
  can_complete: boolean;
}

/**
 * Create challenge input
 */
export interface CreateChallengeInput {
  weekend_id?: string;
  title: string;
  description?: string;
  challenge_type: ChallengeType;
  target_type: ChallengeTargetType;
  visibility: ChallengeVisibility;
  visible_to_team_id?: string;
  visible_to_user_ids?: string[];
  has_points: boolean;
  points?: number;
  bonus_points?: number;
  available_from?: string;
  available_until?: string;
  time_limit_minutes?: number;
  status?: ChallengeStatus;
  config: ChallengeConfig;
  linked_event_id?: string;
  reveal_targets?: string[];
  notification_lottie?: string;
}

/**
 * Challenge icons mapping
 */
export const CHALLENGE_ICONS: Record<ChallengeType, string> = {
  photo: "camera",
  quiz: "help-circle",
  location_checkin: "location",
  task: "checkmark-circle",
  puzzle: "extension-puzzle",
  opdracht: "list",
};

/**
 * Challenge labels mapping
 */
export const CHALLENGE_LABELS: Record<ChallengeType, string> = {
  photo: "Foto opdracht",
  quiz: "Quiz vraag",
  location_checkin: "Locatie check-in",
  task: "Opdracht",
  puzzle: "Puzzle",
  opdracht: "Opdracht",
};

/**
 * Challenge colors mapping
 */
export const CHALLENGE_COLORS: Record<ChallengeType, string> = {
  photo: "#8B5CF6", // Purple
  quiz: "#3B82F6", // Blue
  location_checkin: "#10B981", // Green
  task: "#F59E0B", // Amber
  puzzle: "#EC4899", // Pink
  opdracht: "#6366F1", // Indigo
};

/**
 * Section type icons
 */
export const SECTION_ICONS: Record<SectionType, string> = {
  checkbox: "checkbox",
  photo: "camera",
  text: "document-text",
  number: "calculator",
  location: "location",
};

/**
 * Section type labels
 */
export const SECTION_LABELS: Record<SectionType, string> = {
  checkbox: "Taak",
  photo: "Foto",
  text: "Tekst",
  number: "Getal",
  location: "Locatie",
};

/**
 * Section type colors
 */
export const SECTION_COLORS: Record<SectionType, string> = {
  checkbox: "#10B981", // Green
  photo: "#8B5CF6", // Purple
  text: "#3B82F6", // Blue
  number: "#F59E0B", // Amber
  location: "#EF4444", // Red
};

